/* global PLATFORM, G */
import { asyncPipeSpread, asyncPipeSpreadIf, getFirstItem, isStr } from 'lib/util'
import sequenceModuleAdapterSessionStateMetaSet from 'lib/sequence/module/adapter/session/meta'
import sequenceModuleAdapterSessionStateNavigationReset from 'lib/sequence/module/adapter/session/navigation'
import find from 'lib/sequence/component/children/find'
import { sequenceAppSessionStateRoleSet } from 'lib/sequence/app/session/state/meta'
import validate from 'lib/sequence/model/validate'
import { disable } from 'lib/sequence/component/state/disabled'
import session from 'app/_shared/session'
import validateRedirect from 'app/guest/action/validateRedirect'
import appSettings from 'config/app'
import settingsInit from 'lib/sequence/app/init/settings'

/**
 * Returns the correct route based on the length of `contexts`. In addition, it potentially
 * sets `lastContext` in the user settings
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @param {string[]} contexts           list of available contexts
 * @returns {{action: string}|{action: *}|{action: *}}
 * @private
 */
const _getApplicableContextRoute = (module, contexts) => {
  const settingsAdapter = module[G.ADAPTER][G.SETTINGS]
  const { lastContext = null } = settingsAdapter[G.DATA] || {}

  if (contexts?.length === 1) { // one context
    const [contextName] = contexts
    module[G.ADAPTER][G.ROUTER].context(contextName)
    session(module).context(contextName)

    if (lastContext !== contextName) {
      settingsAdapter[G.API].set({ key: 'lastContext', value: contextName })
    }

    return { action: module[G.ACTIONS].access._name }
  }

  if (contexts?.length > 1) { // multiple contexts
    return { action: module[G.ACTIONS].context._name }
  }

  // no contexts
  return appSettings.defaults.noContext
}

/**
 * Returns the correct next route based on context
 *
 * NOTE: Uses the model to get the settings because the settings adapter hasn't been initialized
 * yet.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @returns {{action: *}|{action: *}|{action: string}}
 * @private
 */
const _getMobileContextRoute = (module) => {
  const meta = getFirstItem(module[G.MODEL][G.ADAPTER]?.[G.SESSION]?.[G.META]) || {}
  const { refs: { settings: fetchedSettings = {} } = {} } = meta
  const { value: { lastContext = '' } = {} } = getFirstItem(fetchedSettings) || {}
  const contexts = session(module).contexts()

  const applicableContexts = contexts.filter(x => appSettings.defaults.mobileContexts.includes(x))
  const targetContexts = applicableContexts.includes(lastContext)
    ? [lastContext]
    : applicableContexts

  return _getApplicableContextRoute(module, targetContexts)
}

/**
 * Returns the correct next route based on context
 *
 * NOTE: Uses the model to get the settings because the settings adapter hasn't been initialized
 * yet.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @returns {{action: *}|{action: *}|{action: string}}
 * @private
 */
const _getWebContextRoute = (module) => {
  const meta = getFirstItem(module[G.MODEL][G.ADAPTER][G.SESSION][G.META]) || {}
  const { refs: { settings: fetchedSettings = {} } = {} } = meta
  const { value: { lastContext = '' } = {} } = getFirstItem(fetchedSettings) || {}
  const contexts = session(module).contexts()

  const targetContexts = contexts.includes(lastContext)
    ? [lastContext]
    : contexts

  return _getApplicableContextRoute(module, targetContexts)
}

/**
 * Sets the next route according to the number of context available to the user:
 *  - If it has a single context, sets it and redirects the user to the access action
 *  - If it has more than one context, redirects the user to the context selection action
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const setNextRoute = module => async (components, ...args) => {
  const { component } = components

  const targetFn = (PLATFORM === 'mobile' && _getMobileContextRoute)
      || (PLATFORM === 'web' && _getWebContextRoute)

  return await validateRedirect(module)(component)(targetFn(module), ...args)
}

/**
 * Initializes the user session by:
 *  - resetting the navigation state,
 *  - setting the user's role
 *  - storing the login payload
 *  - setting the user's language
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const startSession = module => async (components, ...args) => {
  const modelAdapter = module[G.MODEL][G.ADAPTER] // login response
  // TODO: Refactor with SP-957 / SP-956
  sequenceModuleAdapterSessionStateNavigationReset(module, [])
  const userSession = sequenceAppSessionStateRoleSet(module, modelAdapter[G.SESSION][G.META])
  sequenceModuleAdapterSessionStateMetaSet(module, userSession)
  // delete modelAdapter[G.SESSION] // TODO: why?
  // Init settings adapter
  await settingsInit(module)()
  // setting user's language
  const person = session(module).person.ref()
  const { lang } = person?.value || {}
  lang && await module[G.ADAPTER][G.INTL].set(lang)
  return [components, ...args]
}

/**
 * Validates the username and password fields of the login form.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const validateForm = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { form, options } = components
  // validating username, password and remember me fields
  if (PLATFORM !== 'mobile') {
    await validate(model, options) // there is no `options` component in mobile
  }
  await validate(model, form)

  const moduleState = module[G.STATE]
  moduleState[G.ERROR] = model[G.STATE][G.ERROR]
  return [components, ...args]
}

/**
 * Enables or disables the model's username validator according to the value of `enable`.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @param {boolean} enable              whether to enable or disable the username validator
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const toggleUsernameValidator = (module, enable) => async (components, ...args) => {
  const model = module[G.MODEL]
  const validator = model[G.VALIDATOR].username
  disable(validator, !enable)
  return [components, ...args]
}

/**
 * Attempts to initialize a new user session according to the values inserted in the login form.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition
 * @returns {function(): function(...[*]): Promise<*>}
 */
export default module => () => async (...args) => asyncPipeSpread(
  toggleUsernameValidator(module, false),
  validateForm(module),
  asyncPipeSpreadIf(() => !module[G.STATE][G.ERROR])(
    startSession(module),
    setNextRoute(module),
  ),
  toggleUsernameValidator(module, true),
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)
